import React, { useState, useEffect } from "react";
import { useHistory } from "react-router-dom";
import "./index.css";

const BASE = process.env.REACT_APP_URL || "http://127.0.0.1:8000";
const API_KEY = process.env.REACT_APP_API_KEY;
const apiHeaders = () => ({
  "Content-Type": "application/json",
  ...(API_KEY ? { "x-api-key": API_KEY } : {}),
  "ngrok-skip-browser-warning": "true",
});

async function fetchMarketSummary() {
  const res = await fetch(`${BASE}/api/market-summary/`, { headers: apiHeaders() });
  if (!res.ok) throw new Error("Market summary unavailable");
  return res.json();
}

const fmtPrice = (v) => (v == null || isNaN(v) ? "—" : Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));
const fmtVol   = (v) => (v == null || isNaN(v) ? "—" : Number(v).toLocaleString());

/* ── icons ── */
const IconBars = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <line x1="6" y1="20" x2="6" y2="13" /><line x1="12" y1="20" x2="12" y2="5" /><line x1="18" y1="20" x2="18" y2="9" />
  </svg>
);
const IconUp = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 17 9 11 13 15 21 7" /><polyline points="15 7 21 7 21 13" />
  </svg>
);
const IconDown = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 7 9 13 13 9 21 17" /><polyline points="15 17 21 17 21 11" />
  </svg>
);
const Arrow = () => (
  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <line x1="5" y1="12" x2="19" y2="12" /><polyline points="12 5 19 12 12 19" />
  </svg>
);

const Panel = ({ icon, title, accent, rows, kind, onRow, viewAll, onViewAll }) => {
  const maxVol = kind === "volume" ? Math.max(...rows.map(r => r.volume || 0), 1) : 0;
  return (
    <div className="mo-panel">
      <div className="mo-head">
        <div className="mo-head-l">
          <span className={`mo-ic ${accent}`}>{icon}</span>
          <h3>{title}</h3>
        </div>
        {viewAll && <button className="mo-viewall" onClick={onViewAll}>View All <Arrow /></button>}
      </div>

      <div className="mo-cols">
        <span className="l">Symbol</span>
        <span className="r">Price</span>
        <span className="r">{kind === "volume" ? "Volume" : "Change %"}</span>
      </div>

      <div className="mo-rows">
        {rows.map((r) => {
          const chg = r.change_percent != null ? parseFloat(r.change_percent) : null;
          return (
            <div className="mo-row" key={r.code} onClick={() => onRow(r.code)}>
              <span className={`mo-sym ${accent}`}>{r.code}</span>
              <span className="r mo-price">{fmtPrice(r.last_price)}</span>
              {kind === "volume" ? (
                <span className="r mo-vol">
                  {fmtVol(r.volume)}
                  <span className="mo-bar"><i style={{ width: `${Math.max(6, (r.volume / maxVol) * 100)}%` }} /></span>
                </span>
              ) : (
                <span className="r">
                  <span className={`mo-pill ${chg >= 0 ? "up" : "down"}`}>
                    {chg >= 0 ? "+" : ""}{chg?.toFixed(2)}%
                  </span>
                </span>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MarketOverview = ({ viewAll = "/market-overview" }) => {
  const history = useHistory();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchMarketSummary().then(setData).catch(() => setError(true));
  }, []);

  const goSymbol = (code) => history.push(`/trading-dashboard/${code}`);
  const goViewAll = () => viewAll && history.push(viewAll);

  if (error) return null;
  if (!data) return <div className="mo-loading">Loading market data…</div>;

  return (
    <div className="mo-grid">
      <Panel
        icon={<IconBars />} title="Volume Leaders" accent="orange" kind="volume"
        rows={(data.most_active || []).slice(0, 10)} onRow={goSymbol}
        viewAll={!!viewAll} onViewAll={goViewAll}
      />
      <Panel
        icon={<IconUp />} title="Top Gainers" accent="green" kind="change"
        rows={(data.gainers || []).slice(0, 10)} onRow={goSymbol}
        viewAll={!!viewAll} onViewAll={goViewAll}
      />
      <Panel
        icon={<IconDown />} title="Top Losers" accent="red" kind="change"
        rows={(data.losers || []).slice(0, 10)} onRow={goSymbol}
        viewAll={!!viewAll} onViewAll={goViewAll}
      />
    </div>
  );
};

export default MarketOverview;
